export enum LOCALES {
    Indonesian = "id",
    ChineseSimplified = "zh-cn",
    ChineseTraditional = "zh-tw",
    English = "en-gb",
    French = "fr",
    German = "de",
    Italian = "it",
    Japanese = "ja",
    Korean = "ko",
    Lithuanian = "lt",
    Polish = "pl",
    Portuguese = "pt",
    Russian = "ru",
    Spanish = "es",
    Ukrainian = "uk"
}

export enum ROUTES {
    Companies = "/companies",
    Dashboard = "/dashboard",
    DesktopLicenses = "/desktopLicenses",
    DeviceGroups = "/groups",
    DeviceModels = "/deviceModels",
    DeviceModelTaskCompatibilities = "/deviceModelTaskCompatibilities",
    Devices = "/devices",
    Error = "/error",
    Files = "/files",
    Login = "/",
    Maintenance = "/maintenance",
    NotFound = "*",
    ProductCodes = "/productCodes",
    RequestReset = "/requestResetPassword",
    Reset = "/resetPassword",
    Roles = "/roles",
    Signup = "/signup/:token",
    TaskGroups = "/batches",
    Users = "/users"
}

export enum ENDPOINTS {
    AccessGroups = "accessGroups",
    BackgroundActions = "backgroundActions",
    Banners = "banners",
    Batches = "batches",
    CanAdapterTypes = "seoCanAdapterTypes",
    CanAdapters = "canAdapters",
    Companies = "companies",
    CompanyLevels = "companyLevels",
    CustomResourceLimits = "companies/customResourceLimits",
    DesktopLicenses = "desktop/licenses",
    DeviceModels = "deviceModels",
    DeviceModelTaskCompatibilities = "deviceModelTaskCompatibilities",
    DeviceExport = "devices/export",
    Devices = "devices",
    DeviceStats = "devices/stats",
    Files = "files",
    Groups = "groups",
    Login = "login",
    Logout = "logout",
    Messages = "messages",
    Notifications = "notifications",
    OfflineLicenses = "desktop/offlineLicenses",
    ProductCodes = "productCodes",
    RequestResetPassword = "requestResetPassword",
    ResetPassword = "resetPassword",
    Roles = "roles",
    Settings = "settings",
    TaskPlatforms = "tasks/platformSupportTable",
    Tasks = "tasks",
    TfaConfirm = "users/tfa/confirm",
    TfaDisable = "users/tfa/disable",
    TfaGenerateKey = "users/tfa/generateKey",
    TfaGenerateRecoveryCodes = "users/tfa/generateRecoveryCodes",
    TfaSendEmail = "users/tfa/sendEmail",
    Tokens = "tokens",
    Users = "users",
    UsersSelf = "users/self",
    UsersSelfDelete = "users/self/delete",
    Vehicles = "canAdapters/vehicles",
    WidgetStats = "tasks/widgetStats"
}

export enum ENDPOINT_PATH_SEGMENTS {
    AcceptInvitation = "/acceptInvitation",
    Active = "/active",
    AddMissingCompatibilities = "/addMissingCompatibilities",
    Attributes = "/attributes",
    AttributeUpdate = "/attributeUpdate",
    BulkCreate = "/bulkCreate",
    BulkUpdate = "/bulkUpdate",
    Changes = "/changes",
    Clone = "/clone",
    CompatibilityCheck = "/compatibilityCheck",
    ConfigurationChanges = "/configurationChanges",
    Create = "/create",
    DeviceTransferToken = "/deviceTransferToken",
    Edit = "/edit",
    FilterList = "/filterList",
    MarkAllRead = "/markAllRead",
    Me = "/me",
    Move = "/move",
    Properties = "/properties",
    RealtimeEventServiceToken = "/realtimeEventServiceToken",
    Secrets = "/secrets",
    Tfa = "/tfa"
}

export enum REDIRECTIONS {
    LoginEntraId = "login/entraId"
}

export enum TABLE_NAMES {
    Changes = "changes", // inner table
    Companies = "companies",
    ConfigurationChanges = "configurationChanges",
    ConfigurationOverview = "configurationOverview",
    DesktopLicenses = "desktopLicenses",
    DeviceGroups = "groups",
    DeviceModels = "deviceModels",
    DeviceModelTaskCompatibilities = "deviceModelTaskCompatibilities",
    Devices = "devices",
    Files = "files",
    OfflineLicenses = "offlineLicenses", // inner table
    Permissions = "permissions", // inner table
    ProductCodes = "productCodes",
    Roles = "roles",
    TaskGroups = "batches",
    Tasks = "tasks", // inner table
    Users = "users"
}

export enum TABLE_NAMES_WITH_FILTERS {
    Companies = "companies",
    DeviceDetailConfigChanges = "device detail config changes",
    DeviceDetailTasks = "device detail tasks",
    DeviceGroups = "groups",
    DeviceGroupDetailTasks = "device group detail tasks",
    DeviceModels = "deviceModels",
    DeviceModelTaskCompatibilities = "deviceModelTaskCompatibilities",
    Devices = "devices",
    Files = "files",
    Permissions = "permissions",
    Roles = "roles",
    TaskGroupDetailTasks = "task group details tasks",
    TaskGroups = "batches",
    Users = "users"
}

export enum COLUMN_IDENTIFIERS {
    ConfigurationChangesChangedAt = "configurationChangesChangedAt",
    ConfigurationChangesDownloadAt = "configurationChangesDownloadAt",
    ConfigurationType = "configurationType",
    DesktopLicenseStatus = "desktopLicenseStatus",
    DeviceActivityStatus = "deviceActivityStatus",
    DeviceTaskQueue = "deviceTaskQueue",
    DevicesModelPlatform = "devicesModelPlatform",
    ModelsModelPlatform = "modelsModelPlatform",
    RolePermissions = "rolePermissions",
    Roles = "roles",
    RoleStatuses = "roleStatuses",
    Schedule = "schedule",
    SyncedAt = "syncedAt",
    SyncMode = "syncMode",
    SyncStatus = "syncStatus",
    TaskStatusId = "taskStatusId",
    TaskSupport = "taskSupport",
    TaskTypeCompatibilities = "taskTypeCompatibilities",
    UserAccessGroups = "userAccessGroups",
    UserStatus = "userStatus",
    UserSentConfiguration = "userSentConfiguration",
    Visibility = "visibility",
    WebEditorSupport = "webEditorSupport"
}

export enum COLUMN_NAMES {
    AccessGroup = "access group",
    AccessLevel = "access level",
    BatchId = "batch id",
    CanPackage = "can package",
    ChangedAt = "changed at",
    ChangeOrigin = "change origin",
    Code = "code",
    Company = "company",
    Compatibility = "compatibility",
    Configuration = "configuration",
    ConfigurationType = "configuration type",
    CreatedAt = "created at",
    CreatedBy = "created by",
    Description = "description",
    DeviceCompany = "device company",
    DeviceImei = "device imei",
    DevicesCount = "devices count",
    DownloadAt = "download at",
    Email = "email",
    Expiration = "expiration",
    ExpiresAt = "expires at",
    Field = "field",
    FileType = "file type",
    Firmware = "firmware",
    Group = "group",
    GroupVipStatus = "group vip status",
    GsmNumber = "gsm number",
    Iccid = "iccid",
    Id = "id",
    Imei = "imei",
    Imsi = "imsi",
    InvitedBy = "invited by",
    LastCheckedAt = "last checked at",
    LastLoginAt = "last sign in",
    LastStatusUpdate = "last status update",
    LastUsedAt = "last used at",
    Message = "message",
    Model = "model",
    ModelPlatform = "platform",
    ModemVersion = "modem version",
    Name = "name",
    Origin = "origin",
    ParentCompany = "parent company",
    PermissionCount = "permission count",
    ProductCode = "product code",
    Queue = "queue",
    Role = "role",
    RoleName = "role name",
    Schedule = "schedule",
    SeenAt = "seen at",
    Serial = "serial",
    Size = "size",
    Status = "status",
    SupportedTaskTypes = "supported task types",
    SyncedAt = "synced at",
    SyncMode = "sync mode",
    SyncStatus = "sync status",
    TaskId = "task id",
    TaskType = "task type",
    TctWebSupport = "tct web support",
    Type = "type",
    UpdatedAt = "updated at",
    UpdatedBy = "updated by",
    UserSentConfiguration = "user sent configuration",
    ValueFrom = "value from",
    ValueTo = "value to",
    VipStatus = "vip status",
    Visibility = "visibility"
}

export enum COLUMN_RESOURCES {
    AccessGroup = "access_group",
    AccessGroups = "access_groups",
    AccessLevel = "access_level",
    Attribute = "attribute",
    BatchId = "batch_id",
    CanPackage = "can_package",
    ChangedAt = "changed_at",
    Code = "code",
    Company = "company",
    CompanyName = "company_name",
    ConfigurationChange = "configurationChange",
    CreatedAt = "created_at",
    CreatedBy = "created_by",
    CreatedByEmail = "created_by_email",
    CurrentConfiguration = "current_configuration",
    CurrentFirmware = "current_firmware",
    Description = "description",
    DesktopLicenseCreated = "desktopLicenseCreated",
    DesktopLicenseStatusIcon = "enabled",
    Device = "device",
    DeviceImei = "device_imei",
    DevicesCount = "devices_count",
    DeviceStatusIcon = "activity_status",
    Email = "email",
    ExpiresAt = "expires_at",
    File = "file",
    GroupName = "group_name",
    GroupVipStatus = "group_vip_status",
    GsmNumber = "gsm_number",
    Iccid = "iccid",
    Id = "id",
    Imei = "imei",
    Imsi = "imsi",
    Invitation = "invitation",
    InvitedBy = "invited_by",
    LastCheckedAt = "last_checked_at",
    LastLoginAt = "last_login",
    LastUsedAt = "last_used_at",
    Message = "message",
    Model = "model",
    ModelName = "model_name",
    ModelPlatform = "model_platform",
    ModemVersion = "modem_version",
    Name = "name",
    New = "new",
    Old = "old",
    Origin = "origin",
    Permissions = "permissions",
    Platform = "platform",
    ProductCode = "product_code",
    Roles = "roles",
    RoleStatuses = "roleStatuses",
    QueueIcon = "task_queue",
    Schedule = "schedule",
    SeenAt = "seen_at",
    Serial = "serial",
    Size = "size",
    Status = "status",
    StatusChangedAt = "status_changed_at",
    Task = "task",
    TaskStatusIcon = "status_id",
    TaskSupport = "task_support",
    TaskType = "task_type",
    TaskTypeCompatibilities = "task_type_compatibilities",
    Type = "type",
    UpdatedAt = "updated_at",
    UpdatedBy = "updated_by",
    UpdatedByEmail = "updated_by_email",
    UserSentConfiguration = "userSentConfiguration",
    VipStatus = "vip_status",
    Visibility = "visibility",
    WebEditorSupport = "web_editor_support"
}

export enum SORT_RESOURCES {
    BatchId = COLUMN_RESOURCES.BatchId,
    CanPackage = COLUMN_RESOURCES.CanPackage,
    Code = COLUMN_RESOURCES.Code,
    CompanyName = COLUMN_RESOURCES.CompanyName,
    CreatedAt = COLUMN_RESOURCES.CreatedAt,
    CreatedBy = COLUMN_RESOURCES.CreatedBy,
    CreatedByEmail = COLUMN_RESOURCES.CreatedByEmail,
    CurrentCfg = COLUMN_RESOURCES.CurrentConfiguration,
    CurrentFw = COLUMN_RESOURCES.CurrentFirmware,
    Description = COLUMN_RESOURCES.Description,
    DeviceImei = COLUMN_RESOURCES.DeviceImei,
    DevicesCount = COLUMN_RESOURCES.DevicesCount,
    Email = COLUMN_RESOURCES.Email,
    Enabled = COLUMN_RESOURCES.DesktopLicenseStatusIcon,
    ExpiresAt = COLUMN_RESOURCES.ExpiresAt,
    GroupName = COLUMN_RESOURCES.GroupName,
    Id = COLUMN_RESOURCES.Id,
    Imei = COLUMN_RESOURCES.Imei,
    InvitedBy = COLUMN_RESOURCES.InvitedBy,
    LastCheckedAt = COLUMN_RESOURCES.LastCheckedAt,
    Model = COLUMN_RESOURCES.Model,
    Name = COLUMN_RESOURCES.Name,
    New = COLUMN_RESOURCES.New,
    Old = COLUMN_RESOURCES.Old,
    ProductCode = COLUMN_RESOURCES.ProductCode,
    SeenAt = COLUMN_RESOURCES.SeenAt,
    Serial = COLUMN_RESOURCES.Serial,
    Size = COLUMN_RESOURCES.Size,
    StatusChangedAt = COLUMN_RESOURCES.StatusChangedAt,
    Type = COLUMN_RESOURCES.Type,
    UpdatedAt = COLUMN_RESOURCES.UpdatedAt,
    UpdatedBy = COLUMN_RESOURCES.UpdatedBy,
    UpdatedByEmail = COLUMN_RESOURCES.UpdatedByEmail
}

export enum FILTER_NAMES {
    AccessGroup = "access_group_id",
    ActivatedDevices = "first_login_at_date",
    ActivityStatus = "activity_status",
    AllPermissions = "allPermissions",
    Approvable = "approvable",
    BatchId = "batch_id",
    CompanyId = "company_id",
    CompanyType = "company_type",
    ConfigurationFileId = "configuration_file_id",
    CreatedBy = "created_by",
    CurrentFw = "current_firmware",
    CurrentCfg = "current_configuration",
    DeviceImei = "device_imei",
    FirmwareFileId = "firmware_file_id",
    FtBluetoothAuthListFileId = "ft_bluetooth_auth_list_file_id",
    FtConfigurationFileId = "ft_configuration_file_id",
    FtFirmwareFileId = "ft_firmware_file_id",
    FtOneWireAuthListFileId = "ft_one_wire_auth_list_file_id",
    GroupId = "group_id",
    Id = "id",
    ImportedDevices = "created_at_date",
    IncludeStatic = "include_static",
    IsLatest = "is_latest",
    Model = "model",
    ModelName = "model_name",
    ModelPlatform = "model_platform",
    Origin = "origin",
    Permission = "permission",
    Platform = "platform",
    RoleId = "role_id",
    RootCompany = "root",
    SpecId = "spec_id",
    Status = "status",
    SupportedTaskType = "supported_task_type",
    TaskQueue = "task_queue",
    TaskStatus = "status_id",
    TaskSupport = "task_support",
    TaskType = "task_type",
    Type = "type",
    WebEditorSupport = "web_editor_support"
}

export enum NOT_FILTER_NAMES {
    Order = "order",
    Page = "page",
    PerPage = "per_page",
    Query = "query",
    QueryField = "query_field",
    Selected = "selected",
    Sort = "sort"
}

export enum TRANSLATABLE_FILTER_NAMES {
    ActivityStatus = "activity_status",
    AllPermissions = "allPermissions",
    BatchTaskType = "task_type",
    ConfigurationChangeType = "configurationChangeType",
    FileType = "fileType",
    ModelPlatform = "model_platform",
    Origin = "origin",
    Permission = "permission",
    PermissionStatus = "status",
    Platform = "platform",
    SupportedTaskType = "supported_task_type",
    TaskQueue = "task_queue",
    TaskStatus = "status_id",
    TaskType = "taskType",
    Type = "type"
}

export enum FILE_TYPES {
    AdasFw = "adas_firmware",
    ApxFw = "apx_firmware",
    BlackBox = "black_box",
    BleFw = "ble_fw",
    BlueNrg = "blue_nrg",
    BluetoothAuthorizationList = "bluetooth_authorization_list",
    BundleBleDtb = "fw_bundle_ble_dtb",
    BundleBleFw = "fw_bundle_ble",
    BundleCanDtb = "fw_bundle_can_dtb",
    BundleCanFw = "fw_bundle_can",
    BundleMainDtb = "fw_bundle_dtb",
    BundleMainFw = "fw_bundle_mainfw",
    CanChipFw = "can_chip_firmware",
    CanDebugLog = "can_debug_log",
    CarrierApxFw = "carrier_apx_firmware",
    CarrierApxSettings = "carrier_apx_settings",
    CarrierAdvanceFw = "carrier_advance_firmware",
    CarrierAdvanceSettings = "carrier_advance_settings",
    CarrierExtractions = "carrier_extractions",
    CarrierLoggerFiles = "carrier_logger_files",
    Certificate = "certificate",
    Configuration = "configuration",
    CrashDump = "crash_dump",
    DeviceBulkAttributeUpdate = "device_bulk_attribute_update",
    DeviceExportFileCsv = "device_export_file_csv",
    DeviceExportFileXlsx = "device_export_file_xlsx",
    DeviceImportFile = "DeviceImportFile",
    DsmFw = "dsm_firmware",
    Dtb = "dtb",
    ExceptionInfo = "exception_info",
    Firmware = "firmware",
    FirmwareBundle = "fw_bundle",
    FmObdChipFw = "obd_chip_firmware",
    Generic = "generic",
    GlobalFw = "global_firmware",
    GnssmDownloadAssistant = "gnssm_download_assistant",
    GnssmFw = "gnssm_firmware",
    IdCollection = "id_collection_file",
    Log = "log",
    ManualGeofenceList = "manual_geofence_list",
    MeiGModemFw = "meig_modem_firmware",
    NetprismaModemFirmware = "netprisma_modem_firmware",
    NmeaLog = "nmea_log",
    OneWireAuthorizationList = "1_wire_authorization_list",
    Psub = "psub",
    QuectelModemFw = "quectel_modem_firmware",
    SimcomModemFirmware = "simcom_modem_firmware",
    WdLog = "wd_log"
}

export enum TASK_TYPES {
    RxAutoscan = "RxAutoscan",
    RxBlackBox = "RxBlackBox",
    RxBluetoothAuthorizationList = "RxBluetoothAuthorizationList",
    RxCanDebugLog = "RxCanDebugLog",
    RxCarrierExtractions = "RxCarrierExtractions",
    RxCarrierLoggerFiles = "RxCarrierLoggerFiles",
    RxConfiguration = "RxConfiguration",
    RxCrashDump = "RxCrashDump",
    RxExceptionInfo = "RxExceptionInfo",
    RxLog = "RxLog",
    RxManualGeofenceList = "RxManualGeofenceList",
    RxNmeaLog = "RxNmeaLog",
    RxOneWireAuthorizationList = "RxOneWireAuthorizationList",
    RxWdLog = "RxWdLog",
    SetBindSim = "SetBindSim",
    SetBluetoothPassword = "SetBluetoothPassword",
    SetDisableFeature = "SetDisableFeature",
    SetEnableFeature = "SetEnableFeature",
    SetForceLockSim = "SetForceLockSim",
    SetUnbindSim = "SetUnbindSim",
    TxAdasFirmware = "TxAdasFirmware",
    TxAgpsMt3333 = "TxAgpsMt3333",
    TxAgpsQuectelL89 = "TxAgpsQuectelL89",
    TxAutoscanCanConfiguration = "TxAutoscanCanConfiguration",
    TxBleFirmware = "TxBleFirmware",
    TxBlueNrgFirmware = "TxBlueNrgFirmware",
    TxBluetoothAuthorizationList = "TxBluetoothAuthorizationList",
    TxBundleBle = "TxBundleBle",
    TxBundleBleDtb = "TxBundleBleDtb",
    TxBundleCan = "TxBundleCan",
    TxBundleCanDtb = "TxBundleCanDtb",
    TxBundleDtb = "TxBundleDtb",
    TxBundleFirmware = "TxBundleFirmware",
    TxCanAdapterModeChange = "TxCanAdapterModeChange",
    TxCanAdapterModeChangeInternal = "TxCanAdapterModeChangeInternal",
    TxCanAdapterUpdate = "TxCanAdapterUpdate",
    TxCanAdapterUpdateInternal = "TxCanAdapterUpdateInternal",
    TxCanChipFirmware = "TxCanChipFirmware",
    TxCanConfiguration = "TxCanConfiguration",
    TxCarrierAdvanceFirmware = "TxCarrierAdvanceFirmware",
    TxCarrierAdvanceSettings = "TxCarrierAdvanceSettings",
    TxCarrierApxFirmware = "TxCarrierApxFirmware",
    TxCarrierApxSettings = "TxCarrierApxSettings",
    TxConfiguration = "TxConfiguration",
    TxDsmFirmware = "TxDsmFirmware",
    TxDtb = "TxDtb",
    TxFirmware = "TxFirmware",
    TxFirmwareBundle = "TxFirmwareBundle",
    TxFirmwareBundleParts = "TxFirmwareBundleParts",
    TxFotaCertificate = "TxFotaCertificate",
    TxGenericFile = "TxGenericFile",
    TxGNSSMDownloadAssistant = "TxGNSSMDownloadAssistant",
    TxGNSSMFirmware = "TxGNSSMFirmware",
    TxManualGeofenceList = "TxManualGeofenceList",
    TxMeigModemDeltaFirmware = "TxMeigModemDeltaFirmware",
    TxNetprismaModemDeltaFirmware = "TxNetprismaModemDeltaFirmware",
    TxObdChipFirmware = "TxObdChipFirmware",
    TxObdConfig = "TxObdConfig",
    TxOneWireAuthorizationList = "TxOneWireAuthorizationList",
    TxPSub = "TxPSub",
    TxQuectelModemDeltaFirmware = "TxQuectelModemDeltaFirmware",
    TxSimcomModemDeltaFirmware = "TxSimcomModemDeltaFirmware",
    TxUserCertificate = "TxUserCertificate"
}

export enum TASK_STATUSES {
    Pending,
    Running,
    Completed,
    CompletedWithErrors,
    Cancelled,
    Failed,
    Expired,
    PendingApproval,
    Verifying
}

export enum TRI_SOURCES {
    Selected = "id_list",
    Filtered = "filter",
    FromFile = "file"
}

export enum LANGUAGES {
    ChineseSimplified = "zh-CN",
    ChineseTraditional = "zh-TW",
    English = "en-US",
    French = "fr-FR",
    German = "de-DE",
    Italian = "it-IT",
    Japanese = "ja-JP",
    Korean = "ko-KR",
    Lithuanian = "lt-LT",
    Polish = "pl-PL",
    Portuguese = "pt-BR",
    Russian = "ru-RU",
    Spanish = "es-ES",
    Ukrainian = "uk-UA",
    Indonesian = "id-ID"
}

export enum DEVICE_STATISTICS {
    ActivityStatus = "activity_status",
    Model = "model",
    CurrentFw = "current_firmware",
    CurrentCfg = "current_configuration"
}

export enum ACTIVITY_STATUS {
    Inactive = "Inactive",
    Offline = "Offline",
    Online = "Online"
}

export enum TASK_QUEUE {
    Empty = "Empty",
    Pending = "Pending",
    Executing = "Executing"
}

export enum ID_TYPE {
    Imei = "imei",
    Id = "id",
    Type = "type"
}

export enum FILE_ID_TYPE {
    Imei = "imei",
    SerialId = "sn"
}

export enum PER_PAGE {
    SmallCount = "25",
    MediumCount = "50",
    LargeCount = "100"
}
export enum FILE_UPLOAD_STATUS {
    Success = "success",
    Failed = "failed",
    Ready = "ready"
}

export enum FILE_COUNT {
    File = "file",
    Files = "files"
}

export enum LINK_IDS {
    Users = "users",
    Groups = "groups",
    Files = "files",
    Devices = "devices",
    Tasks = "tasks",
    ChildCompanies = "child companies"
}

export enum ORDERS {
    Ascending = "asc",
    Descending = "desc"
}

export enum COOKIE_NAMES {
    All = "all",
    Analytics = "analytics"
}

export enum ENVIRONMENTS {
    Development = "development", // local
    DevelopmentLocalApi = "developmentLocalApi", // local
    Dev = "dev", // DEV
    TrDev = "trDev", // Turkish DEV
    Staging = "staging", // TEST
    Production = "production", // Production
    TrProduction = "trProduction" // Turkish Production
}

export enum API_MESSAGES {
    GroupWasAdded = "Group was added."
}

export enum FILTERS_WITH_QUOTES {
    CurrentCfg = FILTER_NAMES.CurrentCfg,
    CurrentFw = FILTER_NAMES.CurrentFw,
    Model = FILTER_NAMES.Model
}

export enum COMPANY_LEVELS {
    Internal = "Internal",
    Basic = "Basic",
    Standard = "Standard",
    Premium = "Premium"
}

export enum COMPANY_TYPES {
    Internal = "Internal",
    Client = "Client",
    Virtual = "Virtual"
}

export enum COMPANY_LEVEL_SETTINGS {
    FeatureSettings = "feature_settings",
    TimeLimitSettings = "time_limit_settings",
    ResourceLimitSettings = "resource_limit_settings"
}

export enum FEATURE_SETTINGS {
    SmartDeviceGroupSupport = "smart_device_group_support",
    TaskScheduleSupport = "task_schedule_support",
    ApiTokenSupport = "api_token_support",
    UserApiRequestRateLimitPerMinute = "user_api_request_rate_limit_per_minute",
    CustomRoleSupport = "custom_role_support"
}

export enum TIME_LIMIT_SETTINGS {
    TaskExpirationAfterDays = "task_expiration_after_days",
    FileExpirationAfterDays = "file_expiration_after_days",
    DeviceChangeHistoryRetentionPeriodDays = "device_change_history_retention_period_days"
}

export enum RESOURCE_LIMIT_SETTINGS {
    Companies = "companies",
    ConfigurationFilesPerDevice = "configuration_files_per_device",
    TasksPerGroup = "tasks_per_group",
    Users = "users"
}

export enum DROPDOWN_SIZES {
    Tiny = "tiny",
    Small = "small",
    Medium = "medium"
}

export enum USER_ACCESS_LEVELS {
    User = 0,
    Support = 5,
    Admin = 10,
    AdminManager = 15,
    InternalIntegration = 19,
    SystemUser = 20,
    SuperUser = 99
}

export enum AUTHENTICATION_TYPES {
    Authenticator = "Authenticator",
    Email = "Email",
    RecoveryCode = "RecoveryCode"
}

export enum ENABLE_AUTHENTICATION_STEPS {
    SelectMethod,
    GetSecretKey,
    GetEmailCode,
    SendOtpCode,
    SendEmailCode,
    RecoveryCodes,
    SuccessMessage
}

export enum PLATFORMS {
    Ft = "Ft",
    Fmx = "Fmx",
    Legacy = "Legacy",
    Unknown = "Unknown"
}

export enum ROLE_TYPES {
    Custom = "Custom",
    Static = "Static"
}

export enum USER_STATUSES {
    Active = "Active",
    Inactive = "Inactive",
    InvitationExpired = "InvitationExpired",
    InvitationPending = "InvitationPending"
}

export enum BANNER_LEVEL_LABELS {
    Info = "info",
    Warning = "warning"
}

export enum CONFIGURATION_TYPES {
    BluetoothAuthorizationList = "BluetoothAuthorizationList",
    ManualGeofenceList = "ManualGeofenceList",
    MainConfiguration = "MainConfiguration",
    OneWireAuthorizationList = "OneWireAuthorizationList"
}

export enum CONFIGURATION_FEATURE_SETTINGS_FILE_TYPES {
    BluetoothAuthListSync = "bluetooth_auth_list_sync",
    ManualGeofenceListSync = "manual_geofence_list_sync",
    MainConfigurationSync = "main_configuration_sync",
    OneWireAuthListSync = "one_wire_auth_list_sync"
}

export enum CONFIGURATION_FEATURE_SETTINGS_MODES {
    Disabled = "Disabled",
    Enabled = "Enabled",
    ReportingOnly = "ReportingOnly"
}
